import { Routes, Route, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { initAuthListener } from "./features/auth/authThunks";
import { Toaster } from "react-hot-toast";
import Home from "./screens/Home";
import Header from "./components/Header";
import BottomNav from "./components/BottomNav";
import Login from "./components/auth/Login";
import SignUp from "./components/auth/Signup";
import Terms from "./screens/Terms";
import Privacy from "./screens/Privacy";
import Messages from "./screens/Chat/Messages";
import Followings from "./screens/Followings";
import AddCash from "./screens/Payment/AddCash";
import History from "./screens/History";
import BlockedUsers from "./screens/BlockedUsers";
import VideoCall from "./screens/VideoCall";
import MyProfile from "./screens/Profile/MyProfile";
import UserProfile from "./screens/Profile/UserProfile";
import Rating from "./screens/Host/Rating";
import BecomeHost from "./screens/Host/BecomeHost/BecomeHost";
import EarningAndStatistics from "./screens/Host/EarningAndStatistics/EarningAndStatistics";
import PayoutChart from "./screens/Host/EarningAndStatistics/PayoutChart";
import WelcomeMessage from "./screens/Host/WelcomeMessage";
import Pay from "./screens/Payment/Pay";
import TransactionHistory from "./screens/Payment/TransactionHistory";
import Withdraw from "./screens/Host/Withdraw";

import AnswerVideoCall from "./components/AnswerVideoCall";
import CallRating from "./components/CallRating";
import EarningsDetails from "./screens/Host/EarningAndStatistics/EarningsDetails";
import ZohoChat from "./components/supportChat/ZohoChat";

import ProtectedRoute from "./utils/ProtectedRoute";
import Maintenance from "./components/Maintenance";
import MultipleAccount from "./components/MultipleAccount";
import AccountBlockedModal from "./components/AccountBlockedModal";
import useDisableInspect from "./hooks/useDisableInspect";

import ReactGA from "react-ga4";
import RouteTracker from "./analytics/RouteTracker";

function App() {
  const dispatch = useDispatch();
  const call = useSelector((state) => state.call);

  const user = useSelector((state) => state?.auth?.user);
  const loading = useSelector((state) => state?.auth?.loading);
  const isMultipleSession = useSelector((state) => state?.auth?.isMultipleSession);
  const isAccountBlocked = useSelector((state) => state?.auth?.isAccountBlocked);
  const hideBottomNav = useSelector((state) => state?.ui?.hideBottomNav);

  const [isSignUpOpen, setIsSignUpOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  const [maintenance, setMaintenance] = useState(false);

  const location = useLocation();

  const hideHeaderFooterRoutes = ["/privacy", "/terms"];
  const shouldHideHeaderFooter = hideHeaderFooterRoutes.includes(location.pathname);
  useDisableInspect(true);

  useEffect(() => {
    const gaId = process.env.REACT_APP_GA_ID;

    if (process.env.NODE_ENV === "production" && gaId) {
      ReactGA.initialize(process.env.REACT_APP_GA_ID);
    }
  }, []);

  // 🔐 Initialize Firebase Auth Listener ONCE
  useEffect(() => {
    const unsubscribe = dispatch(initAuthListener());

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [dispatch]);

  if (maintenance) {
    return <Maintenance />;
  }

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-black text-white">
        Loading...
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-center" />
      <RouteTracker />
      {call?.status === "incoming" && <AnswerVideoCall />}
      {call?.remote && call?.status === "active" && <VideoCall />}
      {call?.isRatingOpen && user?.role === "user" && <CallRating />}

      {isMultipleSession && <MultipleAccount />}
      {isAccountBlocked && <AccountBlockedModal />}

      {!shouldHideHeaderFooter && (
        <>
        <ZohoChat />
          <Header
            onOpenLogin={() => setIsLoginOpen(true)}
            onOpenSignUp={() => setIsSignUpOpen(true)}
          />
          {!hideBottomNav && <BottomNav />}
        </>
      )}

      <Routes>

        {/* 🌍 PUBLIC ROUTES */}
        <Route path="/" element={<Home openLogin={setIsLoginOpen} />} />
        <Route path="/explore" element={<Home openLogin={setIsLoginOpen} />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />

        <Route
          path="/messages"
          element={
            <ProtectedRoute openLogin={setIsLoginOpen}>
              <Messages />
            </ProtectedRoute>
          }
        />

        <Route
          path="/myProfile"
          element={
            <ProtectedRoute openLogin={setIsLoginOpen}>
              <MyProfile />
            </ProtectedRoute>
          }
        />

        <Route
          path="/user/:userId"
          element={
            <ProtectedRoute openLogin={setIsLoginOpen}>
              <UserProfile />
            </ProtectedRoute>
          }
        />

        <Route
          path="/followings"
          element={
            <ProtectedRoute openLogin={setIsLoginOpen}>
              <Followings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/payment"
          element={
            <ProtectedRoute openLogin={setIsLoginOpen}>
              <AddCash />
            </ProtectedRoute>
          }
        />

        <Route
          path="/pay"
          element={
            <ProtectedRoute openLogin={setIsLoginOpen}>
              <Pay />
            </ProtectedRoute>
          }
        />

        <Route
          path="/transactions"
          element={
            <ProtectedRoute openLogin={setIsLoginOpen}>
              <TransactionHistory />
            </ProtectedRoute>
          }
        />

        <Route
          path="/history"
          element={
            <ProtectedRoute openLogin={setIsLoginOpen}>
              <History />
            </ProtectedRoute>
          }
        />

        <Route
          path="/blockedUsers"
          element={
            <ProtectedRoute openLogin={setIsLoginOpen}>
              <BlockedUsers />
            </ProtectedRoute>
          }
        />

        <Route
          path="/rating"
          element={
            <ProtectedRoute openLogin={setIsLoginOpen}>
              <Rating />
            </ProtectedRoute>
          }
        />

        <Route
          path="/becomeHost"
          element={
            <ProtectedRoute openLogin={setIsLoginOpen}>
              <BecomeHost />
            </ProtectedRoute>
          }
        />

        <Route
          path="/earnings"
          element={
            <ProtectedRoute openLogin={setIsLoginOpen}>
              <EarningAndStatistics />
            </ProtectedRoute>
          }
        />

        <Route
          path="/payout-chart"
          element={
            <ProtectedRoute openLogin={setIsLoginOpen}>
              <PayoutChart />
            </ProtectedRoute>
          }
        />

        <Route
          path="/welcomeMessage"
          element={
            <ProtectedRoute openLogin={setIsLoginOpen}>
              <WelcomeMessage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/earnings-details"
          element={
            <ProtectedRoute openLogin={setIsLoginOpen}>
              <EarningsDetails />
            </ProtectedRoute>
          }
        />

        <Route
          path="/withdraw"
          element={
            <ProtectedRoute openLogin={setIsLoginOpen}>
              <Withdraw />
            </ProtectedRoute>
          }
        />
      </Routes >

      {isLoginOpen && (
        <Login
          onClose={() => setIsLoginOpen(false)}
          onOpenSignup={() => setIsSignUpOpen(true)}
        />
      )
      }

      {
        isSignUpOpen && (
          <SignUp
            onClose={() => setIsSignUpOpen(false)}
            onOpenLogin={() => setIsLoginOpen(true)}
          />
        )
      }
    </>
  );
}

export default App;
